import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { casamapaPartnerVendorManagePath } from "../../constants/casamapaPartnerVendorPaths";
import { casamapaVendorPublicProfilePath } from "../../constants/casamapaVendorWorkspacePaths";
import { useLocale } from "../../intl";
import { fetchCasaMapaPartnerVendors } from "../../services/casamapaPartnerAgencyApi";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/partnerVendorsPage.css";
import "../../styles/partnerCasaMapaTools.css";
import "../../styles/partnerListingsDashboard.css";

function publicationBadgeClass(status) {
  if (status === "published") return "published";
  if (status === "ready_for_review") return "review";
  if (status === "unpublished" || status === "suspended") return "suspended";
  return "draft";
}

function formatDate(value, locale) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(locale);
}

function DirectoryVendorCard({ vendor, t, locale, clientViewId, highlight }) {
  const publicationKey = vendor.publicationStatus || "draft";

  return (
    <article className={`partner-vendor-card${highlight ? " partner-vendor-card--priority" : ""}`}>
      {highlight ? (
        <p className="cm-partner-listings__queue-start">
          {t("realEstate.casaMapa.admin.partnerListings.startHere")}
        </p>
      ) : null}
      <div className="partner-vendor-card__head">
        <span className="partner-vendor-card__mark" aria-hidden="true">
          {String(vendor.name || "?").slice(0, 1)}
        </span>
        <div className="partner-vendor-card__identity">
          <h3>{vendor.name}</h3>
          <p className="partner-vendor-card__slug">{vendor.slug}</p>
        </div>
        <span
          className={`partner-vendor-card__badge partner-vendor-card__badge--${publicationBadgeClass(publicationKey)}`}
        >
          {t(`realEstate.casaMapa.admin.vendors.publicationStatus.${publicationKey}`, {}, publicationKey)}
        </span>
      </div>

      <dl className="partner-vendor-card__meta">
        <div>
          <dt>{t("realEstate.casaMapa.admin.vendors.table.category")}</dt>
          <dd>{vendor.category || "—"}</dd>
        </div>
        <div>
          <dt>{t("realEstate.casaMapa.admin.vendors.table.municipality")}</dt>
          <dd>{vendor.municipality || "—"}</dd>
        </div>
        <div>
          <dt>{t("realEstate.casaMapa.admin.vendors.table.completeness")}</dt>
          <dd>{vendor.profileCompleteness != null ? `${vendor.profileCompleteness}%` : "—"}</dd>
        </div>
        <div>
          <dt>{t("realEstate.casaMapa.admin.vendors.table.updated")}</dt>
          <dd>{formatDate(vendor.updatedAt, locale)}</dd>
        </div>
      </dl>

      <div className="partner-vendor-card__actions">
        {clientViewId ? (
          <Link
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            to={casamapaPartnerVendorManagePath(clientViewId, vendor.id)}
          >
            {t("realEstate.casaMapa.admin.partnerDirectory.manageProfile")}
          </Link>
        ) : null}
        {clientViewId ? (
          <Link
            className="partner-dashboard-btn"
            to={`${casamapaPartnerVendorManagePath(clientViewId, vendor.id)}#service-areas`}
          >
            {t("realEstate.casaMapa.admin.partnerDirectory.editCoverage")}
          </Link>
        ) : null}
        <a
          className="partner-vendor-card__link"
          href={casamapaVendorPublicProfilePath(vendor.slug)}
          target="_blank"
          rel="noreferrer"
        >
          {t("realEstate.casaMapa.admin.partnerDirectory.viewPublic")}
        </a>
      </div>
    </article>
  );
}

export default function PartnerCasaMapaDirectoryPage() {
  const { path } = usePartnerPortalBase();
  const { t, locale } = useLocale();
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clientViewId, setClientViewId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDirectory = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaPartnerVendors({ limit: 50 });
      setVendors(result.vendors || []);
      setSummary(result.vendorDirectory || null);
      setClientViewId(result.clientViewId || null);
    } catch (err) {
      if (err.status === 403) {
        setError(t("realEstate.casaMapa.admin.errors.noCasaMapaAccess"));
      } else if (err.status === 401) {
        setError(t("realEstate.casaMapa.admin.errors.partnerAuthRequired"));
      } else {
        setError(err.message || t("realEstate.casaMapa.admin.errors.loadVendors"));
      }
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  const publishedCount =
    summary?.publishedVendors ?? vendors.filter((v) => v.publicationStatus === "published").length;
  const draftCount =
    summary?.draftVendors ?? vendors.filter((v) => v.publicationStatus === "draft").length;
  const draftVendor = vendors.find((vendor) => vendor.publicationStatus === "draft");

  const nextAction = useMemo(() => {
    if (draftCount > 0 && draftVendor && clientViewId) {
      return {
        title: t("realEstate.casaMapa.admin.partnerDirectory.nextAction.publishTitle"),
        body: t("realEstate.casaMapa.admin.partnerDirectory.nextAction.publishBody", {
          count: draftCount,
        }),
        cta: t("realEstate.casaMapa.admin.partnerDirectory.nextAction.publishCta"),
        to: casamapaPartnerVendorManagePath(clientViewId, draftVendor.id),
        tone: "urgent",
      };
    }
    if (publishedCount > 0) {
      return {
        title: t("realEstate.casaMapa.admin.partnerDirectory.nextAction.leadsTitle"),
        body: t("realEstate.casaMapa.admin.partnerDirectory.nextAction.leadsBody"),
        cta: t("realEstate.casaMapa.admin.partnerDirectory.nextAction.leadsCta"),
        to: path("leads"),
        tone: "ok",
      };
    }
    return {
      title: t("realEstate.casaMapa.admin.partnerDirectory.nextAction.setupTitle"),
      body: t("realEstate.casaMapa.admin.partnerDirectory.nextAction.setupBody"),
      cta: t("realEstate.casaMapa.admin.partnerDashboard.manageProfile"),
      to: path("profile"),
      tone: "neutral",
    };
  }, [clientViewId, draftCount, draftVendor, path, publishedCount, t]);

  return (
    <div className="cm-partner-directory">
      <header className="cm-partner-tools__hero">
        <p className="cm-partner-tools__eyebrow">
          {t("realEstate.casaMapa.admin.partnerDirectory.eyebrow")}
        </p>
        <h1>{t("realEstate.casaMapa.admin.partnerDirectory.title")}</h1>
        <p className="muted">{t("realEstate.casaMapa.admin.partnerDirectory.intro")}</p>
        <p className="cm-partner-listings__scope" role="status">
          {t("realEstate.casaMapa.admin.partnerDirectory.scopeNote")}
        </p>
      </header>

      {loading ? (
        <p className="muted">{t("realEstate.casaMapa.admin.vendors.loading")}</p>
      ) : error ? (
        <p className="admin-data-health-error">{error}</p>
      ) : (
        <>
          <section
            className={`cm-partner-listings__next partner-panel cm-partner-listings__next--${nextAction.tone}`}
          >
            <div>
              <p className="cm-partner-listings__next-kicker">
                {t("realEstate.casaMapa.admin.partnerDirectory.nextAction.kicker")}
              </p>
              <h2>{nextAction.title}</h2>
              <p>{nextAction.body}</p>
            </div>
            <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={nextAction.to}>
              {nextAction.cta}
            </Link>
          </section>

          <section
            className="partner-kpi-grid"
            aria-label={t("realEstate.casaMapa.admin.partnerDirectory.kpiLabel")}
          >
            <article className="partner-kpi-card partner-kpi-card--green">
              <span className="partner-kpi-card__label">
                {t("realEstate.casaMapa.admin.partnerDirectory.kpi.published")}
              </span>
              <strong className="partner-kpi-card__value">{publishedCount}</strong>
            </article>
            <article className="partner-kpi-card partner-kpi-card--amber">
              <span className="partner-kpi-card__label">
                {t("realEstate.casaMapa.admin.partnerDirectory.kpi.draft")}
              </span>
              <strong className="partner-kpi-card__value">{draftCount}</strong>
            </article>
            <article className="partner-kpi-card partner-kpi-card--blue">
              <span className="partner-kpi-card__label">
                {t("realEstate.casaMapa.admin.partnerDirectory.kpi.total")}
              </span>
              <strong className="partner-kpi-card__value">
                {summary?.totalVendors ?? vendors.length}
              </strong>
            </article>
          </section>

          {vendors.length ? (
            <section className="partner-vendors-page__grid">
              {vendors.map((vendor) => (
                <DirectoryVendorCard
                  key={vendor.id}
                  vendor={vendor}
                  t={t}
                  locale={locale}
                  clientViewId={clientViewId}
                  highlight={Boolean(draftVendor && vendor.id === draftVendor.id)}
                />
              ))}
            </section>
          ) : (
            <section className="partner-panel cm-partner-directory__empty">
              <h2>{t("realEstate.casaMapa.admin.partnerDirectory.emptyTitle")}</h2>
              <p className="muted">{t("realEstate.casaMapa.admin.partnerDirectory.emptyBody")}</p>
              <div className="cm-partner-directory__empty-actions">
                <Link
                  className="partner-dashboard-btn partner-dashboard-btn--primary"
                  to={path("profile")}
                >
                  {t("realEstate.casaMapa.admin.partnerDashboard.manageProfile")}
                </Link>
                <Link className="partner-dashboard-btn" to={path("market")}>
                  {t("realEstate.casaMapa.admin.nav.market")}
                </Link>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
